import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import sortByString, { getIdMap } from '../../helpers/helpers';
import { isInDivisions } from '../../helpers/divisionHelpers';
import { getSelectedEquipmentIds } from '../scheduleHelpers';
import { toEquipmentRow } from './equipmentScheduleHelpers';

const useEquipmentRowContext = () => {
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const settings = useSelector((state) => state.settings.company?.settings);
  const equipmentTypeMap = useMemo(() => getIdMap(equipmentTypes), [equipmentTypes]);
  return useMemo(() => ({ equipmentTypeMap, settings }), [equipmentTypeMap, settings]);
};

/** Every piece of equipment as a resource row, keyed by id. */
export const useEquipmentRowMap = () => {
  const equipment = useSelector((state) => state.equipment.equipment);
  const context = useEquipmentRowContext();
  return useMemo(
    () => getIdMap(equipment.map((equip) => toEquipmentRow(equip, context))),
    [equipment, context],
  );
};

/**
 * Active equipment in the selected divisions, sorted by label and narrowed
 * by the schedule's equipment and equipment type filters when they are set.
 */
export default function useEquipmentRows({ filters = {} } = {}) {
  const equipment = useSelector((state) => state.equipment.equipment);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const context = useEquipmentRowContext();
  const {
    equipment: equipmentFilter,
    equipmentTypes: equipmentTypeFilter,
  } = filters;

  const selectedEquipment = useMemo(() => getSelectedEquipmentIds({
    equipment,
    equipmentTypes,
    selectedEquipmentIds: equipmentFilter,
    selectedEquipmentTypeIds: equipmentTypeFilter,
  }), [equipment, equipmentTypes, equipmentFilter, equipmentTypeFilter]);

  return useMemo(() => (
    equipment
      .filter((equip) => {
        if (!equip.active || !isInDivisions(equip, selectedDivisions)) return false;
        return !selectedEquipment.size || selectedEquipment.has(equip.id);
      })
      .map((equip) => toEquipmentRow(equip, context))
      .sort(sortByString('name'))
  ), [equipment, selectedDivisions, selectedEquipment, context]);
}
